import { getMediaType, getMimeType, validateMediaFileContent } from './validation'
import { createFileBackedMediaHandle } from './media-file-picker'

export interface ExtractedMediaFileEntry {
  handle: FileSystemFileHandle
  file: File
  mediaType: 'video' | 'audio' | 'image' | 'lottie' | 'unknown'
}

export interface ExtractedMediaFileDropResult {
  supported: boolean
  entries: ExtractedMediaFileEntry[]
  errors: string[]
}

function supportsFileSystemDragDrop(dataTransfer: DataTransfer): boolean {
  const firstItem = dataTransfer.items[0]
  return (
    !!firstItem &&
    'getAsFileSystemHandle' in firstItem &&
    typeof firstItem.getAsFileSystemHandle === 'function'
  )
}

async function extractValidMediaFilesFromFiles(
  files: File[],
): Promise<Pick<ExtractedMediaFileDropResult, 'entries' | 'errors'>> {
  const entries: ExtractedMediaFileEntry[] = []
  const errors: string[] = []

  for (const file of files) {
    try {
      const validation = await validateMediaFileContent(file)
      if (!validation.valid) {
        errors.push(`${file.name}: ${validation.error}`)
        continue
      }

      entries.push({
        handle: createFileBackedMediaHandle(file),
        file,
        mediaType: getMediaType(getMimeType(file)),
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to read file'
      errors.push(`${file.name}: ${message}`)
    }
  }

  return { entries, errors }
}

export function formatMediaDropRejectionMessage(errors: string[]): string {
  const count = errors.length
  if (count === 0) return ''

  const examples = errors.slice(0, 3).join('; ')
  const overflow = count > 3 ? `; and ${count - 3} more` : ''
  const subject =
    count === 1 ? '1 dropped item was rejected' : `${count} dropped items were rejected`
  return `${subject}: ${examples}${overflow}.`
}

export async function extractValidMediaFileEntriesFromDataTransfer(
  dataTransfer: DataTransfer,
): Promise<ExtractedMediaFileDropResult> {
  if (!supportsFileSystemDragDrop(dataTransfer)) {
    const files = Array.from(dataTransfer.files ?? [])
    if (files.length > 0) {
      const { entries, errors } = await extractValidMediaFilesFromFiles(files)
      return {
        supported: true,
        entries,
        errors,
      }
    }

    return {
      supported: false,
      entries: [],
      errors: [],
    }
  }

  const items = Array.from(dataTransfer.items)
  const handlePromises: Promise<FileSystemHandle | null>[] = []
  for (const item of items) {
    if ('getAsFileSystemHandle' in item) {
      handlePromises.push(item.getAsFileSystemHandle())
    }
  }

  const rawHandles = await Promise.all(handlePromises)
  const entries: ExtractedMediaFileEntry[] = []
  const errors: string[] = []

  for (const handle of rawHandles) {
    if (!handle) {
      continue
    }

    if (handle.kind !== 'file') {
      const name = 'name' in handle ? handle.name : 'Dropped folder'
      errors.push(`${name}: folders are not supported yet. Drop media files directly.`)
      continue
    }

    const fileHandle = handle as FileSystemFileHandle
    try {
      const file = await fileHandle.getFile()
      const validation = await validateMediaFileContent(file)
      if (!validation.valid) {
        errors.push(`${file.name}: ${validation.error}`)
        continue
      }

      entries.push({
        handle: fileHandle,
        file,
        mediaType: getMediaType(getMimeType(file)),
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to read file'
      errors.push(`Unknown file: ${message}`)
    }
  }

  if (entries.length === 0 && errors.length === 0) {
    const files = Array.from(dataTransfer.files ?? [])
    if (files.length > 0) {
      const fallback = await extractValidMediaFilesFromFiles(files)
      return {
        supported: true,
        entries: fallback.entries,
        errors: fallback.errors,
      }
    }
  }

  return {
    supported: true,
    entries,
    errors,
  }
}
